import { Component, inject, OnInit } from '@angular/core';
import { ControlPanelComponent } from './sections/control-panel/control-panel.component';
import { GridComponent } from './sections/grid/grid.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { WidgetSelectionModal } from '../modals/widget-selection/widget-selection.modal';
import { DashboardPersistenceService } from '../../shared/services/dashboard-persistence.service';
import { WidgetTemplate } from '../widgets/models/widget-template';
import { Widget } from '../widgets/models/widget';
import { filter, forkJoin, mergeMap, of } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { DashboardManagementService } from '../../shared/services/dashboard-management.service';
import { SnackbarService } from '../../shared/services/snackbar.service';
import { MockWidgets } from '../../shared/services/content/mock-data/mock-widgets.const';
import { GridAddress } from '../widgets/models/grid-address';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'via-dashboard',
  standalone: true,
  imports: [
    MatProgressSpinnerModule,
    ControlPanelComponent,
    GridComponent
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
})
export class DashboardComponent implements OnInit {
  private readonly dashboardPersistenceService = inject(DashboardPersistenceService);
  private readonly dashboardManagementService = inject(DashboardManagementService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly matDialog = inject(MatDialog);

  public availableWidgets: WidgetTemplate[] = [];
  public enabledWidgets: Widget[] = [];
  public isLoading = true;
  public isLiveData = true;
  public isEditMode = false;
  public showSelectWidgetsModal = false;
  public readonly allowMockData = environment.allowMockData;

  // Lifecycle Hooks *****************************************************
  ngOnInit() {
    this.setupWidgets();
  }

  // Panel Callbacks *****************************************************
  onMockDataToggled(isMock: boolean) {
    this.isLiveData = !isMock;
    this.setupWidgets();
  }

  onEditModeToggled(isEditMode: boolean) {
    // Edit mode toggles only switch UI state. They should not trigger a widget
    // data refresh or reload when switching back to view mode.
    this.isEditMode = isEditMode;
  }

  // Grid Callbacks ******************************************************
  onWidgetDisabled(widgetKey: string): void {
    const removedWidget = this.enabledWidgets.find((widget) => widget.widgetKey === widgetKey);
    this.enabledWidgets = this.enabledWidgets.filter((w) => w.widgetKey !== widgetKey);
    this.availableWidgets = this.dashboardManagementService.syncEnabledStatus(
      this.availableWidgets,
      this.enabledWidgets,
    );

    if (this.isLiveData && removedWidget) {
      this.dashboardPersistenceService.removeWidget('MAIN', removedWidget).subscribe({
        error: () => {
          this.snackbarService.notifyError('Widget configuration save failed');
        }
      });
    }
  }

  saveWidgetStates(widgets: Widget[]) {
    this.enabledWidgets = this.cloneWidgets(widgets);
    this.availableWidgets = this.dashboardManagementService.syncEnabledStatus(
      this.availableWidgets,
      this.enabledWidgets,
    );

    if (!this.isLiveData) {
      return;
    }

    const updateWidgetCalls = this.enabledWidgets.map((widget: Widget) =>
      this.dashboardPersistenceService.saveWidgetPosition('MAIN', widget)
    );

    forkJoin(updateWidgetCalls).subscribe({
      next: () => {
        this.snackbarService.notifySuccess('Widget configuration saved successfully');
      },
      error: () => {
        this.snackbarService.notifyError('Widget configuration save failed');
      }
    });
  }

  showWidgetSelection() {
    const dialogRef = this.matDialog.open(WidgetSelectionModal, {
      width: '50vw',
      maxHeight: '70vh',
      data: {
        availableWidgets: this.availableWidgets
      }
    });

    // Wait for dialog to close, and if it closes with a 'SAVE' result, save the widget selection.
    dialogRef.afterClosed().pipe(
      filter((result: string) => result === 'SAVE'),
      mergeMap(() => {
        const result = this.dashboardManagementService.updateEnabledWidgets(this.availableWidgets, this.enabledWidgets);
        if (!result) {
          return of(undefined);
        }

        this.enabledWidgets = this.cloneWidgets(result.enabledWidgets);

        if (!this.isLiveData) {
          return of(undefined);
        }

        return this.dashboardPersistenceService.saveWidgetSelection('MAIN', result.enabledWidgets, result.removedWidgets);
      })
    ).subscribe({
      next: () => {
        if (this.isLiveData) {
          this.snackbarService.notifySuccess('Widget configuration saved successfully');
        }
      },
      error: () => {
        this.snackbarService.notifyError('Widget configuration save failed');
      }
    });
  }

  // Private Methods *****************************************************
  private setupWidgets() {
    this.isLoading = true;

    if (!this.isLiveData) {
      this.setupMockWidgets();
      return;
    }

    this.dashboardPersistenceService.getAvailableWidgetTemplates().subscribe({
      next: (widgets: WidgetTemplate[]) => {
        this.availableWidgets = widgets;
        // Only sync if enabled widgets have already finished loading.
        if (this.enabledWidgets) {
          this.availableWidgets = this.dashboardManagementService.syncEnabledStatus(this.availableWidgets, this.enabledWidgets);
        }
      },
      error: () => {
        this.snackbarService.notifyError('Available widget configuration failed to load');
        this.isLoading = false;
      }
    });

    this.dashboardPersistenceService.getDashboardWidgets('MAIN').subscribe({
      next: (widgets: Widget[]) => {
        this.enabledWidgets = this.cloneWidgets(widgets);
        // Only sync if available widgets have already finished loading.
        if (this.availableWidgets) {
          this.availableWidgets = this.dashboardManagementService.syncEnabledStatus(this.availableWidgets, this.enabledWidgets);
        }
        this.isLoading = false;
      },
      error: () => {
        this.snackbarService.notifyError('Dashboard widget configuration failed to load');
        this.isLoading = false;
      }
    });
  }

  private setupMockWidgets(): void {
    const mockWidgets = this.cloneWidgets(MockWidgets);

    this.enabledWidgets = mockWidgets.filter((widget: Widget) => widget.enabled);
    this.availableWidgets = this.dashboardManagementService.syncEnabledStatus(
      mockWidgets.map((widget: Widget) => this.toWidgetTemplate(widget)),
      this.enabledWidgets,
    );

    this.isLoading = false;
  }

  private toWidgetTemplate(widget: Widget): WidgetTemplate {
    return {
      key: widget.widgetKey,
      widgetTemplateName: widget.widgetName,
      title: widget.title,
      defaultWidth: widget.defaultWidth,
      defaultHeight: widget.defaultHeight,
      widgetType: widget.widgetType,
      showHeader: widget.showHeader,
      showFooter: widget.showFooter,
      tables: widget.tables ?? [],
      enabled: !!widget.enabled,
    };
  }

  private cloneWidgets(widgets: Widget[]): Widget[] {
    return widgets.map((widget: Widget) => ({
      ...widget,
      tables: [...(widget.tables ?? [])],
      gridAddress: this.isValidGridAddress(widget.gridAddress)
        ? {
          topLeft: { ...widget.gridAddress.topLeft },
          bottomRight: { ...widget.gridAddress.bottomRight },
        }
        : undefined as unknown as GridAddress,
      isDragging: false,
    }));
  }

  private isValidGridAddress(address: GridAddress | undefined): address is GridAddress {
    return !!address?.topLeft?.column
      && !!address?.topLeft?.row
      && !!address?.bottomRight?.column
      && !!address?.bottomRight?.row;
  }
}
